"use client";

import type { CustomerServiceFormData } from "./customer-service-form.schema";

import Loading from "@homeinstead/icons/Loading";
import { nanpPostalCodePattern } from "@homeinstead/locale";
import { Button } from "@homeinstead/ui/components/Button";
import { FormError } from "@homeinstead/ui/components/FormError";
import { Input } from "@homeinstead/ui/components/Input";
import { PhoneInput } from "@homeinstead/ui/components/PhoneInput";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useCustomerServiceOffices } from "~www/hooks/use-customer-service-offices";
import { CustomerServiceFranchiseSelect } from "./CustomerServiceFranchiseSelect";
import { CustomerServiceSmsConsent } from "./CustomerServiceSmsConsent";
import { customerServiceFormSchema } from "./customer-service-form.schema";
import { getOfficeSearchState } from "./get-office-search-state";
import { trackCustomerServiceFormSubmit } from "./track-customer-service-form-submit";
import { useSubmitCustomerServiceFeedback } from "./use-submit-customer-service-feedback";

type CustomerServiceFormProps = {
  thankYouPageUrl: string;
};

function CustomerServiceForm({ thankYouPageUrl }: CustomerServiceFormProps) {
  const methods = useForm<CustomerServiceFormData>({
    defaultValues: {
      emailAddress: "",
      feedback: "",
      firstName: "",
      franchise: "",
      lastName: "",
      optInEmail: true,
      phoneNumber: "",
      postalCode: "",
      smsConsent: false,
    },
    mode: "onTouched",
    resolver: zodResolver(customerServiceFormSchema),
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
  } = methods;

  const postalCodeValue = useWatch({ control, name: "postalCode" });
  const isPostalCodeValid = nanpPostalCodePattern.test(postalCodeValue ?? "");

  const {
    data: locationItems,
    error: hiringOfficeSearchError,
    isFetching: isFetchingHiringOffices,
  } = useCustomerServiceOffices({
    forceProximitySearch: true,
    postalCode: isPostalCodeValid ? postalCodeValue : "",
  });

  const franchiseOptions = (locationItems ?? []).map((item) => ({
    label: item.officeSubtitle,
    value: String(item.id),
  }));

  const officeSearchState = getOfficeSearchState({
    franchiseOptionsCount: franchiseOptions.length,
    hasSearchError: Boolean(hiringOfficeSearchError),
    isFetching: isFetchingHiringOffices,
    isPostalCodeValid,
  });

  const {
    error: submitError,
    isPending: isSubmitting,
    mutate: submitFeedback,
  } = useSubmitCustomerServiceFeedback(thankYouPageUrl);

  function onSubmit(data: CustomerServiceFormData) {
    submitFeedback(data);
  }

  function onInvalid() {
    trackCustomerServiceFormSubmit(
      "validation-error",
      methods.getValues("franchise"),
    );
  }

  const submitErrorMessage =
    submitError instanceof Error
      ? submitError.message
      : submitError
        ? "Something went wrong. Please try again."
        : null;

  return (
    <div className="rounded-lg p-0 md:px-6">
      <FormProvider {...methods}>
        <form
          className="flex flex-col gap-3"
          onSubmit={handleSubmit(onSubmit, onInvalid)}
        >
          <fieldset
            className="flex min-w-0 flex-col gap-3 border-none p-0"
            disabled={isSubmitting}
          >
            <div>
              <Input
                placeholder="First name"
                type="text"
                {...register("firstName")}
              />
              {errors.firstName ? (
                <FormError.span className="text-error">
                  {errors.firstName.message}
                </FormError.span>
              ) : null}
            </div>

            <div>
              <Input
                placeholder="Last name"
                type="text"
                {...register("lastName")}
              />
              {errors.lastName ? (
                <FormError.span className="text-error">
                  {errors.lastName.message}
                </FormError.span>
              ) : null}
            </div>

            <PhoneInput<CustomerServiceFormData>
              errorClassName="text-error"
              fieldName="phoneNumber"
              showErrorAfterSubmit
            />

            <div>
              <Input
                placeholder="Email address"
                type="email"
                {...register("emailAddress")}
              />
              {errors.emailAddress ? (
                <FormError.span className="text-error">
                  {errors.emailAddress.message}
                </FormError.span>
              ) : null}
            </div>

            <div>
              <Input
                maxLength={10}
                placeholder="Postal code"
                type="text"
                {...register("postalCode")}
              />
              {errors.postalCode ? (
                <FormError.span className="text-error">
                  {errors.postalCode.message}
                </FormError.span>
              ) : null}
            </div>

            <CustomerServiceFranchiseSelect
              control={control}
              disabled={isSubmitting}
              errors={errors}
              franchiseOptions={franchiseOptions}
              officeSearchState={officeSearchState}
            />

            <div>
              <textarea
                className="min-h-44 w-full resize-y rounded-sm border border-new-beginnings-base bg-white px-3 py-3 text-base text-new-beginnings-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-new-beginnings-base disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Tell us about your experience"
                {...register("feedback")}
              />
              {errors.feedback ? (
                <FormError.span className="text-error">
                  {errors.feedback.message}
                </FormError.span>
              ) : null}
            </div>

            <CustomerServiceSmsConsent control={control} />
          </fieldset>

          {submitErrorMessage ? (
            <FormError.p className="text-error">
              {submitErrorMessage}
            </FormError.p>
          ) : null}

          <Button
            className="mt-1 w-full rounded-full"
            disabled={isSubmitting}
            loading={isSubmitting}
            type="submit"
            variant="primary"
          >
            {isSubmitting ? (
              <Loading className="h-8 w-8 fill-new-beginnings-base" />
            ) : (
              "Submit Message"
            )}
          </Button>
        </form>
      </FormProvider>
    </div>
  );
}

export { CustomerServiceForm };
